import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs/operators';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SessionActions } from './session.actions';
import { CARGOCHIEF_KEYS, ACCOUNT_TYPE } from '@domain/constants/app-variables';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { IpService } from '@service/ip/ip.service';

@Injectable({
	providedIn: 'root',
})
export class LegacySessionEffects {
	constructor(
		private actions$: Actions<any>,
		private segmentWrapper: SegmentWrapperService,
		private cargoChief: Cargochief,
		private ipService: IpService
	) {}

	loginSetupSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SessionActions.login.setup.success),
				tap(async ({ payload }) => {
					// Write localStorage synchronously before awaiting the IP lookup. The
					// sibling navigation effect (session.effects.ts loginSetupSuccess$)
					// also fires on this action and calls router.navigate() without
					// awaiting anything; if we await here first, the router mounts the
					// landing page (e.g. /carriers/availableLoads) before legacy
					// components' ngOnInit can read loggedInUser / loginSetup.
					const user = { ...payload.user };
					localStorage.setItem('loggedInUser', JSON.stringify(user));
					localStorage.setItem(
						'loginSetup',
						JSON.stringify({
							user: user,
							account: payload.account,
							accountSettings: payload.accountSettings,
							userSettings: payload.userSettings,
							planProducts: payload.products,
							addOnSubscriptions: payload.addOnSubscriptions,
							activeIntegrations: payload.activeIntegrations,
							// CN-8997: the Loads nav reads this via Cargochief.HAS_ACTIVE_TMS_INTEGRATION.
							// This object is a hand-built projection, so a field absent here is absent from
							// localStorage no matter what /loginSetup returned — any new field the app reads
							// off `loginSetup` has to be added to this list and to the session.effects
							// payload above it.
							hasActiveTmsIntegration: payload.hasActiveTmsIntegration,
						})
					);
					this.cargoChief.setItem({ [CARGOCHIEF_KEYS.LOGGED_IN]: true });

					if (
						!payload.account.termsAgreedAt &&
						user?.role?.toLowerCase().indexOf(ACCOUNT_TYPE.OWNER.toLowerCase()) > -1
					) {
						this.cargoChief.setItem({ [CARGOCHIEF_KEYS.UPDATE_PROFILE]: true });
					}

					const ipAddress = await this.ipService.getIpAddress();
					// Guard against a stale continuation: if another login/logout happened
					// while the IP lookup was in flight, the session in localStorage no
					// longer belongs to this user and we must not rewrite it or fire
					// Segment events for the previous session.
					const currentUser = JSON.parse(localStorage.getItem('loggedInUser') || 'null');
					if (currentUser?.id !== user.id) {
						return;
					}
					const userWithIp = { ...user, ipAddress };
					localStorage.setItem('loggedInUser', JSON.stringify(userWithIp));
					const storedLoginSetup = JSON.parse(localStorage.getItem('loginSetup') || '{}');
					localStorage.setItem(
						'loginSetup',
						JSON.stringify({ ...storedLoginSetup, user: userWithIp })
					);

					this.segmentWrapper.fireSegmentIdentify(String(userWithIp.id), userWithIp);
					this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.logged_in, userWithIp);
				})
			),
		{ dispatch: false }
	);
}
